import {Injectable} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {CheckAddressComponent} from '../../../shared/modals/patient/check-address/check-address.component';
import GeocoderResult = google.maps.GeocoderResult;
import {GoogleMapsService} from '../google-maps/google-maps.service';
import {MatDialog} from '@angular/material/dialog';
import {Address} from '../../models/address.model';

@Injectable({
  providedIn: 'root',
})
export class CheckGoogleAddressService {
  constructor(private googleMapsService: GoogleMapsService, private dialog: MatDialog) {}

  checkGeocoderResults(results: GeocoderResult[] = [], address, allowedTypes: string[] = ['street_address']): Observable<any> {
    let {street, zipCode} = address;
    const {city, state, address1, zipcode} = address;
    if (address1 && zipcode && !street && !zipCode) {
      street = address1;
      zipCode = zipcode;
    }
    //const allowedTypes = ['route', 'premise', 'street_address'];
    const patientAddress: Address = {...address, address1: street, zipcode: zipCode};
    const hasStreetAddressType = this.filterResults(results).find(({types}) =>
      allowedTypes.some((type) => types.includes(type))
    );

    delete patientAddress['zipCode'];

    if (!hasStreetAddressType) {
      const { lat: gpsLatitude, lng: gpsLongitude } = results[0].geometry.location.toJSON();
      return of({ ...patientAddress, gpsLatitude, gpsLongitude })
    } else {
      const googleAddress: Address = this.googleMapsService.populateAddress(hasStreetAddressType);
      const addressesEquals = this.compareAddress(googleAddress, patientAddress);

      if (!addressesEquals) {
        const msg = `Did you mean this address: ${googleAddress.address1}, ${googleAddress.city}, ${googleAddress.state}, ${googleAddress.zipcode}`;

        return this.showMessage(msg).pipe(
          map((patientInteraction) => (patientInteraction ? googleAddress : patientAddress)),
          mergeMap((address) => {
            const {address1, city, state, zipcode} = address;

            if (!address1 || !city || !state || !zipcode) {
              return throwError('Address invalid');
            }
            return of(address);
          })
        );
      } else {
        return of(googleAddress);
      }
    }
  }

  filterResults(results: GeocoderResult[] = []) {
    // check if google result has city and state
    // locality                     -> city
    // administrative_area_level_1  -> state
    if (results && results.length) {
      return results.filter(({address_components}) => {
        const hasCity = address_components.some(({types}) => types.includes('locality')); // city
        const hasState = address_components.some(({types}) => types.includes('administrative_area_level_1')); // state

        return hasCity && hasState;
      });
    }
    return [];
  }

  showMessage(msg) {
    const dialogRef = this.dialog.open(CheckAddressComponent, {
      position: {top: '0'},
      data: {msg},
    });

    return dialogRef.afterClosed();
  }

  compareAddress(googleAddress, patientAddress): boolean {
    return (
      googleAddress.address1 === patientAddress.address1 &&
      googleAddress.city === patientAddress.city &&
      googleAddress.zipcode === patientAddress.zipcode
    );
  }
}
